// src/components/pages/CartService.jsx
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getColors } from '../../styles/colors';
import { 
  FaShoppingCart, FaPlus, FaArrowLeft, FaSearch, FaMinus, FaTrash,
  FaFilter, FaTimes, FaLeaf, FaSpinner, FaCheck,
  FaUtensils, FaCreditCard, FaShoppingBag, FaFish
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  fetchProductsAsync, 
  filterProductsAsync,
  resetFilters,
  filterByType,
  searchProducts,
  clearError
} from '../../redux/slices/productSlice';
import { 
  addToCartAsync, 
  fetchCartItemsAsync, 
  removeFromCartAsync, 
  updateCartAsync
} from '../../redux/slices/cartSlice';

const CartService = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useSelector((state) => state.theme?.mode || 'light');
  const isDark = theme === 'dark';
  const colors = getColors(isDark);
  
  // Get products from Redux
  const { 
    products, 
    filteredProducts, 
    isLoading, 
    error, 
    activeFilter,
    isFiltering
  } = useSelector((state) => state.products || {
    products: [],
    filteredProducts: [],
    isLoading: false,
    error: null,
    activeFilter: null,
    isFiltering: false
  });
  
  // Get cart state from Redux
  const { 
    items: cartItems, 
    totalItems, 
    totalPrice, 
    isLoading: cartLoading 
  } = useSelector((state) => state.cart || {
    items: [],
    totalItems: 0,
    totalPrice: 0,
    isLoading: false
  });
  
  const [searchQuery, setSearchQuery] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Fetch products and cart on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      dispatch(fetchProductsAsync());
      dispatch(fetchCartItemsAsync());
    } else {
      console.warn('⚠️ No auth token found. Please login.');
    }
  }, [dispatch]);

  // Handle search
  useEffect(() => {
    if (searchQuery.trim()) {
      dispatch(searchProducts(searchQuery));
    } else {
      if (activeFilter) {
        dispatch(filterByType(activeFilter));
      } else {
        dispatch(searchProducts(''));
      }
    }
  }, [searchQuery, dispatch, activeFilter]);

  // Handle filter selection
  const handleFilterSelect = async (type) => {
    setSelectedFilter(type);
    setShowFilter(false);
    setSearchQuery('');
    
    if (type === 'all') {
      dispatch(resetFilters());
      dispatch(fetchProductsAsync());
      return;
    }
    
    try {
      const filterData = { type: type };
      await dispatch(filterProductsAsync(filterData)).unwrap();
      
      setSuccessMessage(`Showing ${type} products`);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
      
    } catch (err) {
      console.error('Filter failed:', err);
      alert(`Failed to filter ${type} products. Please try again.`);
    }
  };

  // ✅ POST - Add to cart
  const handleAddToCart = async (product) => {
    setIsUpdating(true);
    try {
      await dispatch(addToCartAsync({
        product_id: product.id,
        quantity: 1,
        price: product.price
      })).unwrap();
      
      await dispatch(fetchCartItemsAsync());
      
      const productName = product.product_name || 'Product';
      setSuccessMessage(`✅ Added ${productName} to cart!`);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
      
    } catch (err) {
      console.error('❌ Failed to add to cart:', err);
      alert('Failed to add item to cart. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  // ✅ POST - Update quantity in cart - FIXED: Use item_id (product ID)
  const handleUpdateQuantity = async (cartId, newQuantity) => {
    if (newQuantity < 1) return;
    setIsUpdating(true);
    try {
      // ✅ Find the item to get the product ID (item_id)
      const cartItem = cartItems.find(item => item.cart_id === cartId);
      if (!cartItem) {
        console.error('❌ Cart item not found:', cartId);
        alert('Item not found in cart.');
        setIsUpdating(false);
        return;
      }
      
      const productId = parseInt(cartItem.item_id);
      console.log('📤 Updating quantity - Cart ID:', cartId, 'Product ID:', productId, 'New Quantity:', newQuantity);
      
      // ✅ Use product ID (item_id) for update, not cart_id
      await dispatch(updateCartAsync({
        item_id: productId,
        quantity: newQuantity
      })).unwrap();
      
      console.log('✅ Quantity updated successfully');
      
    } catch (err) {
      console.error('❌ Failed to update quantity:', err);
      alert('Failed to update quantity. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  // ✅ DELETE - Remove from cart
  const handleRemoveItem = async (cartId) => {
    if (!window.confirm('Remove this item from cart?')) return;
    setIsUpdating(true);
    try {
      console.log('📤 Removing item with cart_id:', cartId);
      
      await dispatch(removeFromCartAsync(cartId)).unwrap();
      
      setSuccessMessage('🗑️ Item removed from cart');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
      
    } catch (err) {
      console.error('❌ Failed to remove item:', err);
      alert('Failed to remove item. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  // ✅ FIXED: Get cart quantity with proper type comparison
  const getCartQuantity = (productId) => {
    const productIdStr = String(productId);
    const item = cartItems.find(item => {
      const itemIdStr = String(item.item_id);
      return itemIdStr === productIdStr && item.item_type === 'product';
    });
    return item ? parseInt(item.quantity) : 0;
  };

  // ✅ FIXED: Get cart id with proper type comparison
  const getCartId = (productId) => {
    const productIdStr = String(productId);
    const item = cartItems.find(item => {
      const itemIdStr = String(item.item_id);
      return itemIdStr === productIdStr && item.item_type === 'product';
    });
    return item ? item.cart_id : null;
  };

  // ✅ Check if product is in cart
  const isProductInCart = (productId) => {
    const productIdStr = String(productId);
    return cartItems.some(item => {
      const itemIdStr = String(item.item_id);
      return itemIdStr === productIdStr && item.item_type === 'product';
    });
  };

  // Calculate total
  const calculateTotal = () => {
    return cartItems.reduce((sum, item) => sum + (parseFloat(item.price) * parseInt(item.quantity)), 0);
  };

  const cartTotal = totalPrice || calculateTotal();

  // Check if user is authenticated
  const hasValidToken = !!localStorage.getItem('token');

  // Get display products
  const displayProducts = filteredProducts.length > 0 ? filteredProducts : products;

  // Clear errors
  const handleClearError = () => {
    dispatch(clearError());
  };

  // Render filter button text
  const getFilterLabel = () => {
    if (activeFilter === 'Veg') return 'Veg';
    if (activeFilter === 'Non-Veg') return 'Non-Veg';
    return 'Filter';
  };

  // Render filter icon
  const getFilterIcon = () => {
    if (activeFilter === 'Veg') return <FaLeaf className="text-green-500" />;
    if (activeFilter === 'Non-Veg') return <FaUtensils className="text-red-500" />;
    return <FaFilter />;
  };

  // Get product type icon
  const getProductTypeIcon = (type) => {
    if (type === 'Veg' || type === 'veg') {
      return <FaLeaf className="text-green-500" />;
    } else if (type === 'Non-Veg' || type === 'nonveg' || type === 'NonVeg') {
      return <FaFish className="text-red-500" />;
    }
    return <FaUtensils className="text-yellow-500" />;
  };

  if (!hasValidToken) {
    return (
      <div className="min-h-screen flex items-center justify-center" 
           style={{ backgroundColor: colors.background }}>
        <div className="text-center p-8 rounded-2xl max-w-md w-full"
             style={{ 
               backgroundColor: colors.card,
               border: `1px solid ${colors.border}`
             }}>
          <div className="text-5xl mb-4">🔐</div>
          <h2 className="text-2xl font-bold mb-2" style={{ color: colors.text }}>
            Please Login
          </h2>
          <p className="mb-6" style={{ color: colors.textSecondary }}>
            You need to be logged in to view products.
          </p>
          <button 
            onClick={() => navigate('/login')}
            className="px-6 py-3 rounded-full text-white font-medium transition-all duration-300 hover:scale-105"
            style={{ backgroundColor: colors.primary }}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (isLoading && displayProducts.length === 0 && !isFiltering) {
    return (
      <div className="min-h-screen flex items-center justify-center" 
           style={{ backgroundColor: colors.background }}>
        <div className="text-center">
          <div className="w-12 h-12 border-4 rounded-full animate-spin mx-auto"
               style={{ 
                 borderColor: colors.primary,
                 borderTopColor: 'transparent'
               }}></div>
          <p className="mt-4 font-medium" style={{ color: colors.textSecondary }}>
            Loading products...
          </p>
        </div>
      </div>
    );
  }

  if (error && displayProducts.length === 0) {
    const errorMessage = typeof error === 'string' ? error : 'Something went wrong';
    
    return (
      <div className="min-h-screen flex items-center justify-center" 
           style={{ backgroundColor: colors.background }}>
        <div className="text-center p-8 rounded-2xl max-w-md w-full"
             style={{ 
               backgroundColor: colors.card,
               border: `1px solid ${colors.border}`
             }}>
          <div className="text-4xl mb-4">😕</div>
          <h3 className="text-xl font-bold mb-2" style={{ color: colors.text }}>
            Failed to Load Products
          </h3>
          <p className="mb-4" style={{ color: colors.textSecondary }}>
            {errorMessage}
          </p>
          <div className="flex gap-3 justify-center">
            <button 
              onClick={() => dispatch(fetchProductsAsync())}
              className="px-6 py-2 rounded-full text-white font-medium transition-all duration-300 hover:scale-105"
              style={{ backgroundColor: colors.primary }}
            >
              Try Again
            </button>
            <button 
              onClick={handleClearError}
              className="px-6 py-2 rounded-full font-medium transition-all duration-300 hover:scale-105"
              style={{ 
                backgroundColor: colors.inputBackground,
                color: colors.text
              }}
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
      {/* Success Toast */}
      <AnimatePresence>
        {showSuccess && (
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 px-6 py-3 rounded-xl shadow-lg flex items-center gap-3"
            style={{ 
              backgroundColor: '#22C55E',
              color: 'white'
            }}
          >
            <FaCheck />
            {successMessage}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero Section */}
      <div className="relative pt-12 pb-8 px-4 text-center"
           style={{
             background: `linear-gradient(180deg, ${colors.background} 0%, ${colors.background} 100%)`
           }}>
        <h1 className="text-4xl md:text-5xl font-bold mb-3" style={{ color: colors.text }}>
          🛒 Cart Service
        </h1>
        <p className="text-lg" style={{ color: colors.textSecondary }}>
          Browse products and add to your cart
        </p>
        
        {/* Cart Count */}
        <p className="text-sm mt-2" style={{ color: colors.textSecondary }}>
          {cartItems.length} items in cart • {displayProducts.length} products available
          {activeFilter && ` • Filtered: ${activeFilter}`}
          {isFiltering && ' • Filtering...'}
        </p>
      </div>
      
      <div className="container mx-auto px-4 py-6">
        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 mb-6 px-4 py-2 rounded-full transition-all hover:scale-105"
          style={{ 
            backgroundColor: colors.card,
            border: `1px solid ${colors.border}`,
            color: colors.text
          }}
        >
          <FaArrowLeft />
          <span>Back to Home</span>
        </button>

        {/* Search and Filter Bar */}
        <div className="flex gap-3 mb-6">
          <div className="relative flex-1">
            <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2" 
                      style={{ color: colors.textSecondary }} />
            <input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full py-3 pl-12 pr-4 rounded-xl focus:outline-none"
              style={{ 
                backgroundColor: colors.inputBackground || colors.card,
                border: `1px solid ${colors.border}`,
                color: colors.text
              }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 transform -translate-y-1/2"
                style={{ color: colors.textSecondary }}
              >
                <FaTimes className="text-sm" />
              </button>
            )}
          </div>
          
          {/* Filter Button */}
          <div className="relative">
            <button
              onClick={() => setShowFilter(!showFilter)}
              className="px-4 py-3 rounded-xl transition-all hover:scale-105 flex items-center gap-2"
              style={{ 
                backgroundColor: activeFilter ? colors.primary : colors.card,
                border: `1px solid ${activeFilter ? colors.primary : colors.border}`,
                color: activeFilter ? '#FFFFFF' : colors.text
              }}
              disabled={isFiltering}
            >
              {isFiltering ? (
                <FaSpinner className="animate-spin" />
              ) : (
                getFilterIcon()
              )}
              <span className="hidden sm:inline">
                {isFiltering ? 'Filtering...' : getFilterLabel()}
              </span>
              {activeFilter && (
                <span className="ml-1 text-xs bg-white/20 px-1.5 py-0.5 rounded-full">
                  {displayProducts.length}
                </span>
              )}
            </button>
            
            {/* Filter Dropdown */}
            {showFilter && (
              <div 
                className="absolute right-0 mt-2 w-52 rounded-xl shadow-lg z-10 overflow-hidden"
                style={{ 
                  backgroundColor: colors.card,
                  border: `1px solid ${colors.border}`
                }}
              >
                <div className="p-2">
                  <div className="text-xs font-medium px-3 py-1.5" style={{ color: colors.textSecondary }}>
                    Filter by Type
                  </div>
                  <button
                    onClick={() => handleFilterSelect('all')}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all flex items-center gap-3 ${
                      selectedFilter === 'all' ? 'bg-primary/10' : 'hover:bg-primary/5'
                    }`}
                    style={{ 
                      color: selectedFilter === 'all' ? colors.primary : colors.text,
                      backgroundColor: selectedFilter === 'all' ? colors.primary + '20' : 'transparent'
                    }}
                  >
                    <FaTimes className="text-xs" />
                    All Products
                    {selectedFilter === 'all' && <FaCheck className="ml-auto text-xs" />}
                  </button>
                  <button
                    onClick={() => handleFilterSelect('Veg')}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all flex items-center gap-3 ${
                      selectedFilter === 'Veg' ? 'bg-primary/10' : 'hover:bg-primary/5'
                    }`}
                    style={{ 
                      color: selectedFilter === 'Veg' ? '#22C55E' : colors.text,
                      backgroundColor: selectedFilter === 'Veg' ? '#22C55E20' : 'transparent'
                    }}
                  >
                    <FaLeaf className="text-xs" />
                    Veg
                    {selectedFilter === 'Veg' && <FaCheck className="ml-auto text-xs" />}
                  </button>
                  <button
                    onClick={() => handleFilterSelect('Non-Veg')}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all flex items-center gap-3 ${
                      selectedFilter === 'Non-Veg' ? 'bg-primary/10' : 'hover:bg-primary/5'
                    }`}
                    style={{ 
                      color: selectedFilter === 'Non-Veg' ? '#EF4444' : colors.text,
                      backgroundColor: selectedFilter === 'Non-Veg' ? '#EF444420' : 'transparent'
                    }}
                  >
                    <FaUtensils className="text-xs" />
                    Non-Veg
                    {selectedFilter === 'Non-Veg' && <FaCheck className="ml-auto text-xs" />}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Product Grid */}
        {displayProducts.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">🛒</div>
            <h2 className="text-2xl font-bold mb-2" style={{ color: colors.text }}>
              {searchQuery ? 'No products match your search' : 'No products available'}
            </h2>
            <p className="mb-6" style={{ color: colors.textSecondary }}>
              {searchQuery ? 'Try a different search term.' : 'Check back later for new products.'}
            </p>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="px-4 py-2 rounded-full text-white font-medium transition-all hover:scale-105"
                style={{ backgroundColor: colors.primary }}
              >
                Clear Search
              </button>
            )}
            {activeFilter && !searchQuery && (
              <button
                onClick={() => handleFilterSelect('all')}
                className="px-4 py-2 rounded-full text-white font-medium transition-all hover:scale-105 ml-3"
                style={{ backgroundColor: colors.primary }}
              >
                Clear Filter
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="text-sm mb-4 flex justify-between items-center" style={{ color: colors.textSecondary }}>
              <span>
                Showing {displayProducts.length} products
                {activeFilter && ` (${activeFilter})`}
              </span>
              {(isLoading || isFiltering) && (
                <FaSpinner className="animate-spin" style={{ color: colors.primary }} />
              )}
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {displayProducts.map((product, index) => {
                const cartQty = getCartQuantity(product.id);
                const cartId = getCartId(product.id);
                const isInCart = cartQty > 0;
                
                return (
                  <motion.div
                    key={product.id || index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="rounded-xl overflow-hidden shadow-lg"
                    style={{ 
                      backgroundColor: colors.card,
                      border: `1px solid ${colors.border}`
                    }}
                  >
                    {/* Product Image */}
                    <div className="relative h-40 overflow-hidden bg-gray-200">
                      <img
                        src={product.prod_image ? `https://admin.thefoodies.in/public/uploads/products/${product.prod_image}` : '/placeholder-image.jpg'}
                        alt={product.product_name || 'Product'}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = '/placeholder-image.jpg';
                        }}
                      />
                      {product.status === 1 && (
                        <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-xs font-medium text-white"
                             style={{ backgroundColor: '#22C55E' }}>
                          Available
                        </div>
                      )}
                      <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-xs font-medium text-white"
                           style={{ 
                             backgroundColor: product.type === 'Veg' ? '#22C55E' : '#EF4444'
                           }}>
                        <span className="flex items-center gap-1">
                          {getProductTypeIcon(product.type)}
                          {product.type || 'Veg'}
                        </span>
                      </div>
                      {isInCart && (
                        <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded-full text-xs font-medium text-white"
                             style={{ backgroundColor: '#3B82F6' }}>
                          {cartQty} in cart
                        </div>
                      )}
                    </div>
                    
                    {/* Product Details */}
                    <div className="p-3">
                      <h3 className="text-base font-bold mb-1" style={{ color: colors.text }}>
                        {product.product_name || 'Product'}
                      </h3>
                      
                      <div className="text-xs mb-2" style={{ color: colors.textSecondary }}>
                        {product.in_thali ? (
                          <div>
                            {product.in_thali.split('\n').slice(0, 2).map((item, i) => (
                              <div key={i} className="truncate">• {item}</div>
                            ))}
                            {product.in_thali.split('\n').length > 2 && (
                              <span>+{product.in_thali.split('\n').length - 2} more</span>
                            )}
                          </div>
                        ) : (
                          <span className="italic">No description</span>
                        )}
                      </div>
                      
                      <div className="flex justify-between items-center mt-2 pt-2 border-t"
                           style={{ borderColor: colors.border }}>
                        <span className="text-xl font-bold" style={{ color: colors.primary }}>
                          ₹{product.price || '0'}
                        </span>
                        
                        {/* ✅ Show + and - buttons when item is in cart */}
                        {isInCart ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleUpdateQuantity(cartId, cartQty - 1)}
                              disabled={isUpdating || cartLoading}
                              className="w-8 h-8 rounded-full flex items-center justify-center text-white transition-all hover:scale-105 disabled:opacity-50"
                              style={{ backgroundColor: '#EF4444' }}
                            >
                              <FaMinus className="text-xs" />
                            </button>
                            <span className="font-semibold min-w-[24px] text-center text-sm" style={{ color: colors.text }}>
                              {cartQty}
                            </span>
                            <button
                              onClick={() => handleUpdateQuantity(cartId, cartQty + 1)}
                              disabled={isUpdating || cartLoading}
                              className="w-8 h-8 rounded-full flex items-center justify-center text-white transition-all hover:scale-105 disabled:opacity-50"
                              style={{ backgroundColor: '#22C55E' }}
                            >
                              <FaPlus className="text-xs" />
                            </button>
                            <button
                              onClick={() => handleRemoveItem(cartId)}
                              disabled={isUpdating || cartLoading}
                              className="w-8 h-8 rounded-full flex items-center justify-center text-white transition-all hover:scale-105 disabled:opacity-50 ml-1"
                              style={{ backgroundColor: '#EF4444' }}
                            >
                              <FaTrash className="text-xs" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleAddToCart(product)}
                            disabled={isUpdating || cartLoading}
                            className="px-4 py-1.5 rounded-full text-white text-sm font-medium transition-all hover:scale-105 flex items-center gap-1.5"
                            style={{ 
                              backgroundColor: colors.primary,
                              boxShadow: `0 2px 10px ${colors.primary}40`
                            }}
                          >
                            <FaPlus className="text-xs" />
                            Add
                          </button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </>
        )}

        {/* Cart Summary - Show if cart has items */}
        {cartItems.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-8 rounded-xl p-6"
            style={{ 
              backgroundColor: colors.card,
              border: `1px solid ${colors.border}`
            }}
          >
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <div>
                <h3 className="text-lg font-bold" style={{ color: colors.text }}>
                  🛒 Cart Summary
                </h3>
                <p style={{ color: colors.textSecondary }}>
                  {cartItems.length} items • ₹{cartTotal.toFixed(2)}
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => navigate('/checkout-service')}
                  className="px-6 py-3 rounded-full text-white font-medium transition-all hover:scale-105 flex items-center gap-2"
                  style={{ 
                    backgroundColor: colors.primary,
                    boxShadow: `0 4px 15px ${colors.primary}40`
                  }}
                >
                  <FaCreditCard />
                  Checkout
                </button>
                <button
                  onClick={() => navigate('/view-cart')}
                  className="px-6 py-3 rounded-full font-medium transition-all hover:scale-105 flex items-center gap-2"
                  style={{ 
                    backgroundColor: colors.inputBackground,
                    color: colors.text,
                    border: `1px solid ${colors.border}`
                  }}
                >
                  <FaShoppingBag />
                  View Cart
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default CartService;